import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { shallowEqual, useSelector } from "react-redux";
import { useNotification } from "../../../../hooks/useNotification";
import {
  useActivatePromoMutation,
  useDeletePromoMutation,
  useDeactivatePromoMutation,
} from "../../../../store/apiSlice/promoApiSlice";
import { ConfirmationModal } from "../../../../components/organisms/Modal/confirmationModal";
import { componentWithCondition } from "../../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ModalOverlay, ModalT1 } from "../../../../components/organisms/Modal";
import { PortalHeader } from "../../../../components/molecules/Header";
import { ListActionBurgerMenu } from "../../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";
import {
  StyledPromoTypeContainer,
  StyledPromoTypeItem,
  StyledPromoTextContent,
  StyledPromoTitle,
  StyledPromoDescription,
} from "./action.styles";

const rootModals = document.getElementById("root-modals");

const PromoAction = ({ selectedOrderId, setSelectedOrder }) => {
  const navigate = useNavigate();
  const notify = useNotification();
  const { promos } = useSelector(
    ({ promo }) => ({
      promos: promo.promos,
    }),
    shallowEqual,
  );
  const selectedPromo =
    selectedOrderId.length === 1
      ? promos?.results?.find(item => item.id === selectedOrderId[0])
      : null;
  const isReactivateMode = selectedPromo?.status?.id === "inactive";

  const [activatePromo, { isLoading: isActivateLoading } = {}] = useActivatePromoMutation();
  const [deletePromo, { isLoading: isDeleteLoading } = {}] = useDeletePromoMutation();
  const [deactivatePromo, { isLoading: isDeactivateLoading } = {}] = useDeactivatePromoMutation();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handleUpdate = () => {
    if (selectedOrderId.length !== 1) {
      notify({ type: "warning", message: "Please select exactly one promotion to update." });
      return;
    }
    navigate(`/promo/manage/update/${selectedOrderId[0]}`);
  };

  const handleDelete = async () => {
    try {
      await deletePromo(selectedOrderId[0]).unwrap();
    } catch (error) {
      notify({ type: "error", message: error?.detail || "Deletions failed. Please try again." });
    }

    setSelectedOrder([]);
    setIsDeleting(false);
  };

  const handleStatusChange = async () => {
    try {
      if (isReactivateMode) {
        await activatePromo(selectedOrderId[0]).unwrap();
      } else {
        await deactivatePromo(selectedOrderId[0]).unwrap();
      }
    } catch (error) {
      notify({
        type: "error",
        message:
          error?.data?.detail ||
          `${
            isReactivateMode ? "Reactivation" : "Deactivation"
          } failed. Please try again.`,
      });
    }

    setSelectedOrder([]);
    setIsDeactivating(false);
  };

  const statusActionLabel = isReactivateMode ? "Reactivate" : "Deactivate";
  const statusActionTitle = `${statusActionLabel} Promotion`;
  const statusActionDescription = `Are you sure you want to ${statusActionLabel.toLowerCase()} selected promotion?`;
  const isStatusActionLoading = isReactivateMode ? isActivateLoading : isDeactivateLoading;

  const burgerItems = useMemo(
    () => [
      {
        key: "toggle-status",
        label: statusActionLabel,
        onClick: () => setIsDeactivating(true),
        disabled: selectedOrderId.length !== 1 || isStatusActionLoading,
        title:
          selectedOrderId.length === 1
            ? ""
            : `Select a single promotion to ${statusActionLabel.toLowerCase()}.`,
        isLoading: isStatusActionLoading,
      },
      {
        key: "delete",
        label: "Delete",
        onClick: () => setIsDeleting(true),
        disabled: selectedOrderId.length !== 1 || isDeleteLoading,
        title: selectedOrderId.length === 1 ? "" : "Select a single promotion to delete.",
        isLoading: isDeleteLoading,
        isDanger: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusActionLabel, selectedOrderId.length, isStatusActionLoading, isDeleteLoading],
  );

  return (
    <>
      <li className={`tableButton activeState`}>
        <button onClick={() => setIsCreating(true)}>Create</button>
      </li>
      <li
        className={`tableButton ${selectedOrderId.length === 1 ? "activeState" : "disableState"}`}
      >
        <button
          onClick={handleUpdate}
          disabled={selectedOrderId.length !== 1}
          title={selectedOrderId.length === 1 ? "" : "Select a single promotion to update."}
        >
          Update
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />

      {isCreating && <SelectPromoTypeModal closeModal={() => setIsCreating(false)} />}

      {isDeactivating && (
        <ConfirmationModal
          titleOfModal={statusActionTitle}
          btnName={statusActionLabel}
          closeModal={() => setIsDeactivating(false)}
          handleModalWork={handleStatusChange}
          description={statusActionDescription}
          isLoading={isStatusActionLoading}
        />
      )}

      {isDeleting && (
        <ConfirmationModal
          titleOfModal="Delete Promotion"
          btnName="Delete"
          closeModal={() => setIsDeleting(false)}
          handleModalWork={handleDelete}
          description={`Are you sure you want to delete selected promotion?`}
          labelForConfirmCheck="Confirm deletion"
          isLoading={isDeleteLoading}
        />
      )}
    </>
  );
};

const SelectPromoTypeModal = ({ closeModal }) => {
  const navigate = useNavigate();

  const PROMO_TYPES = [
    {
      title: "Amount off order",
      description: "Discount the total order amount",
      path: "/promo/create/order_amount",
    },
    {
      title: "Amount off products",
      description: "Discount specific products or product categories",
      path: "/promo/create/product_amount",
    },
    {
      title: "Buy X get Y",
      description:
        "Reward customers who buy a set quantity or amount with a free or discounted item",
      path: "/promo/create/buy_x_get_y",
    },
  ];

  const handleItemClick = path => {
    navigate(path);
    closeModal();
  };

  const modalContent = (
    <ModalOverlay aria-modal="true" role="dialog">
      <ModalT1 $minWidth={480} $maxWidth={560} onClick={e => e.stopPropagation()}>
        <PortalHeader title="Select Promotion Type" onClose={closeModal} />
        <StyledPromoTypeContainer>
          {PROMO_TYPES.map((item, index) => (
            <StyledPromoTypeItem key={index} onClick={() => handleItemClick(item.path)}>
              <StyledPromoTextContent>
                <StyledPromoTitle className="promo-title">{item.title}</StyledPromoTitle>
                <StyledPromoDescription className="promo-desc">
                  {item.description}
                </StyledPromoDescription>
              </StyledPromoTextContent>
              <ChevronRight />
            </StyledPromoTypeItem>
          ))}
        </StyledPromoTypeContainer>
      </ModalT1>
    </ModalOverlay>
  );

  return createPortal(modalContent, rootModals);
};

const ChevronRight = () => (
  <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1 1.5L6.5 7L1 12.5"
      stroke="#B0B0B0"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PromoActionWithCheck = componentWithCondition(PromoAction);
